export type ReasonCompleteEvent =
  | 'negative'
  | 'bank-under-5-years'
  | 'retail-under-5-year'
  | 'old-debt'
  | 'small-debt'
  | 'low-priority'
  | 'no-debts'
  | 'api-error'
  | 'negative-timeout'
  | 'timeout';

export enum DebtTier {
  NegativeDebt = 1,
  BankDebtUnder5Years = 2,
  RetailDebtUnder5Years = 3,
  OldDebt = 4,
  SmallDebtOffer = 5, // This tier is handled as a special case after the initial sort
  LowPriority = 6,
}

export const DebtTierColorModifier: { [key in DebtTier]?: 'red' | 'orange' } = {
  [DebtTier.NegativeDebt]: 'red',
  [DebtTier.BankDebtUnder5Years]: 'orange',
  [DebtTier.RetailDebtUnder5Years]: 'orange',
  [DebtTier.LowPriority]: 'orange',
};

export enum PartnerRole {
  Bank = 'bank',
  Retail = 'retail',
  Securitizing = 'securitizing',
  Telephony = 'telephony',
  Education = 'education',
  Other = 'other',
}

export interface PartnerInfo {
  identifier: string;
  role: PartnerRole;
}

export type ConsultiveStorageType = {
  active: 'consulting' | 'bau' | 'default';
  cjDebtStorage: string;
  cjClusterStorage: DebtTier;
};

export const JOURNEY_STORAGE_KEY = 'ac-cj';

export const Partners: { [key: string]: PartnerInfo } = {
  // --- Banks ---
  BancoDoBrasil: { identifier: 'banco-do-brasil', role: PartnerRole.Bank },
  BancoNext: { identifier: 'banco-next', role: PartnerRole.Bank },
  BancoPan: { identifier: 'banco-pan', role: PartnerRole.Bank },
  Bradescard: { identifier: 'bradescard', role: PartnerRole.Bank },
  Bradesco: { identifier: 'bradesco', role: PartnerRole.Bank },
  Btg: { identifier: 'btg', role: PartnerRole.Bank },
  Bv: { identifier: 'bv', role: PartnerRole.Bank },
  C6bank: { identifier: 'c6bank', role: PartnerRole.Bank },
  Caixa: { identifier: 'caixa', role: PartnerRole.Bank },
  Itau: { identifier: 'itau', role: PartnerRole.Bank },
  Mercadopago: { identifier: 'mercadopago', role: PartnerRole.Bank },
  Neon: { identifier: 'neon', role: PartnerRole.Bank },
  Nubank: { identifier: 'nubank', role: PartnerRole.Bank },
  Omni: { identifier: 'omni', role: PartnerRole.Bank },
  Original: { identifier: 'original', role: PartnerRole.Bank },
  Pagbank: { identifier: 'pagbank', role: PartnerRole.Bank },
  Pan: { identifier: 'pan', role: PartnerRole.Bank },
  Portoseguro: { identifier: 'portoseguro', role: PartnerRole.Bank },
  Santander: { identifier: 'santander', role: PartnerRole.Bank },
  Tribanco: { identifier: 'tribanco', role: PartnerRole.Bank },
  Trigg: { identifier: 'trigg', role: PartnerRole.Bank },
  Willbank: { identifier: 'willbank', role: PartnerRole.Bank },

  // --- Retail ---
  Carrefour: { identifier: 'carrefour', role: PartnerRole.Retail },
  CasasBahia: { identifier: 'casas-bahia', role: PartnerRole.Retail },
  Cencosud: { identifier: 'cencosud', role: PartnerRole.Retail },
  Colombo: { identifier: 'colombo', role: PartnerRole.Retail },
  Credz: { identifier: 'credz', role: PartnerRole.Retail },
  Havan: { identifier: 'havan', role: PartnerRole.Retail },
  Lojasrenner: { identifier: 'lojasrenner', role: PartnerRole.Retail },
  Luizacred: { identifier: 'luizacred', role: PartnerRole.Retail },
  Mercadolivre: { identifier: 'mercadolivre', role: PartnerRole.Retail },
  Pernambucanas: { identifier: 'pernambucanas', role: PartnerRole.Retail },
  Ponto: { identifier: 'ponto', role: PartnerRole.Retail },
  Riachuelo: { identifier: 'riachuelo', role: PartnerRole.Retail },
  Uzecartoes: { identifier: 'uzecartoes', role: PartnerRole.Retail },
  ViaVarejo: { identifier: 'via-varejo', role: PartnerRole.Retail },

  // --- Securitizing ---
  Ativossa: { identifier: 'ativossa', role: PartnerRole.Securitizing },
  Crediativos: { identifier: 'crediativos', role: PartnerRole.Securitizing },
  Credigy: { identifier: 'credigy', role: PartnerRole.Securitizing },
  FundoAtlantico: { identifier: 'fundo-atlantico', role: PartnerRole.Securitizing },
  Ipanema: { identifier: 'ipanema', role: PartnerRole.Securitizing },
  Itapeva: { identifier: 'itapeva', role: PartnerRole.Securitizing },
  Recovery: { identifier: 'recovery', role: PartnerRole.Securitizing },

  // --- Telephony ---
  Claro: { identifier: 'claro', role: PartnerRole.Telephony },
  Net: { identifier: 'net', role: PartnerRole.Telephony },
  Oi: { identifier: 'oi', role: PartnerRole.Telephony },
  Sky: { identifier: 'sky', role: PartnerRole.Telephony },
  Tim: { identifier: 'tim', role: PartnerRole.Telephony },
  Vivo: { identifier: 'vivo', role: PartnerRole.Telephony },

  // --- Education ---
  Ampli: { identifier: 'ampli', role: PartnerRole.Education },
  Anhanguera: { identifier: 'anhanguera', role: PartnerRole.Education },
  Estacio: { identifier: 'estacio', role: PartnerRole.Education },
  Ibmec: { identifier: 'ibmec', role: PartnerRole.Education },
  Kroton: { identifier: 'kroton', role: PartnerRole.Education },
  Pitagoras: { identifier: 'pitagoras', role: PartnerRole.Education },
  Unic: { identifier: 'unic', role: PartnerRole.Education },
  Uniderp: { identifier: 'uniderp', role: PartnerRole.Education },
  Unime: { identifier: 'unime', role: PartnerRole.Education },
  Unopar: { identifier: 'unopar', role: PartnerRole.Education },
  Wyden: { identifier: 'wyden', role: PartnerRole.Education },

  // --- Other ---
  Ac: { identifier: 'ac', role: PartnerRole.Other },
  Acordocerto: { identifier: 'acordocerto', role: PartnerRole.Other },
  AcNew: { identifier: 'ac-new', role: PartnerRole.Other },
  Act: { identifier: 'act', role: PartnerRole.Other },
  Arc4u: { identifier: 'arc4u', role: PartnerRole.Other },
  Asp: { identifier: 'asp', role: PartnerRole.Other },
  Biva: { identifier: 'biva', role: PartnerRole.Other },
  Brk: { identifier: 'brk', role: PartnerRole.Other },
  Cielo: { identifier: 'cielo', role: PartnerRole.Other },
  Credsystem: { identifier: 'credsystem', role: PartnerRole.Other },
  Dacasa: { identifier: 'dacasa', role: PartnerRole.Other },
  DmLogo: { identifier: 'dm-logo', role: PartnerRole.Other },
  Edp: { identifier: 'edp', role: PartnerRole.Other },
  Hoepers: { identifier: 'hoepers', role: PartnerRole.Other },
  Liftcred: { identifier: 'liftcred', role: PartnerRole.Other },
  MgwAtivos: { identifier: 'mgw-ativos', role: PartnerRole.Other },
  Saneaqua: { identifier: 'saneaqua', role: PartnerRole.Other },
};
